import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateMarketEventDTO,
  CreateMarketEventExpenseDTO,
  CreateMarketEventItemDTO,
  IssueEventStockBatchDTO,
  MarketEventListQueryDTO,
  RecordMarketEventSaleDTO,
  StockQuantityDTO,
  UpdateMarketEventExpenseDTO,
  UpdateMarketEventItemDTO,
} from "@/modules/event-sales/domain/contracts";
import { eventSalesApi } from "./client";

const keys = {
  all: ["event-sales"] as const,
  list: (query?: MarketEventListQueryDTO) =>
    [...keys.all, "list", query] as const,
  detail: (id: string) => [...keys.all, "detail", id] as const,
  items: (id: string) => [...keys.all, "items", id] as const,
  suggest: (id: string, q: string) => [...keys.all, "suggest", id, q] as const,
  sales: (id: string) => [...keys.all, "sales", id] as const,
  expenses: (id: string) => [...keys.all, "expenses", id] as const,
  summary: (id: string) => [...keys.all, "summary", id] as const,
  stock: (id: string) => [...keys.all, "stock", id] as const,
  stockVariants: (q: string) => [...keys.all, "stock-variants", q] as const,
};

function useRefreshEventSales() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: keys.all });
}

export function useEventSalesQuery(query?: MarketEventListQueryDTO) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => eventSalesApi.list(query),
  });
}

export function useEventSaleQuery(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => eventSalesApi.get(id),
    enabled: Boolean(id),
  });
}

export function useEventItemsQuery(id: string) {
  return useQuery({
    queryKey: keys.items(id),
    queryFn: () => eventSalesApi.items(id),
    enabled: Boolean(id),
  });
}

export function useEventSuggestQuery(id: string, q: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.suggest(id, q),
    queryFn: () => eventSalesApi.suggest(id, q),
    enabled: Boolean(id) && enabled,
  });
}

export function useEventSalesBillsQuery(id: string) {
  return useQuery({
    queryKey: keys.sales(id),
    queryFn: () => eventSalesApi.sales(id),
    enabled: Boolean(id),
  });
}

export function useEventExpensesQuery(id: string) {
  return useQuery({
    queryKey: keys.expenses(id),
    queryFn: () => eventSalesApi.expenses(id),
    enabled: Boolean(id),
  });
}

export function useEventSummaryQuery(id: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.summary(id),
    queryFn: () => eventSalesApi.summary(id),
    enabled: Boolean(id) && enabled,
  });
}

export function useCreateEventSale() {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (input: CreateMarketEventDTO) => eventSalesApi.create(input),
    onSuccess: refresh,
  });
}

export function useOpenEventSale() {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (id: string) => eventSalesApi.open(id),
    onSuccess: refresh,
  });
}

export function useCloseEventSale() {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (id: string) => eventSalesApi.close(id),
    onSuccess: refresh,
  });
}

export function useDeleteEventSaleEvent() {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (id: string) => eventSalesApi.remove(id),
    onSuccess: refresh,
  });
}

export function useCreateEventItem(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (input: CreateMarketEventItemDTO) =>
      eventSalesApi.createItem(eventId, input),
    onSuccess: refresh,
  });
}

export function useUpdateEventItem(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (input: UpdateMarketEventItemDTO & { itemId: string }) =>
      eventSalesApi.updateItem(eventId, input.itemId, {
        name: input.name,
        defaultPrice: input.defaultPrice,
      }),
    onSuccess: refresh,
  });
}

export function useDeleteEventItem(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (itemId: string) => eventSalesApi.deleteItem(eventId, itemId),
    onSuccess: refresh,
  });
}

export function useRecordEventBill(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (input: RecordMarketEventSaleDTO) =>
      eventSalesApi.recordSale(eventId, input),
    onSuccess: refresh,
  });
}

export function useDeleteEventBill(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (saleId: string) => eventSalesApi.deleteSale(eventId, saleId),
    onSuccess: refresh,
  });
}

export function useCreateEventExpense(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (input: CreateMarketEventExpenseDTO) =>
      eventSalesApi.createExpense(eventId, input),
    onSuccess: refresh,
  });
}

export function useUpdateEventExpense(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (input: UpdateMarketEventExpenseDTO & { expenseId: string }) =>
      eventSalesApi.updateExpense(eventId, input.expenseId, {
        category: input.category,
        amount: input.amount,
        spentOn: input.spentOn,
        note: input.note,
      }),
    onSuccess: refresh,
  });
}

export function useDeleteEventExpense(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (expenseId: string) =>
      eventSalesApi.deleteExpense(eventId, expenseId),
    onSuccess: refresh,
  });
}

export function useEventStockQuery(id: string) {
  return useQuery({
    queryKey: keys.stock(id),
    queryFn: () => eventSalesApi.stockIssues(id),
    enabled: Boolean(id),
  });
}

export function useStockVariantSuggestQuery(q: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.stockVariants(q),
    queryFn: () => eventSalesApi.suggestStockVariants(q),
    enabled,
  });
}

export function useIssueEventStock(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (input: IssueEventStockBatchDTO) =>
      eventSalesApi.issueStock(eventId, input),
    onSuccess: refresh,
  });
}

export function useReturnEventStock(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (input: StockQuantityDTO & { issueId: string }) =>
      eventSalesApi.returnStock(eventId, input.issueId, {
        quantity: input.quantity,
      }),
    onSuccess: refresh,
  });
}

export function useUseEventStock(eventId: string) {
  const refresh = useRefreshEventSales();
  return useMutation({
    mutationFn: (input: StockQuantityDTO & { issueId: string }) =>
      eventSalesApi.consumeStock(eventId, input.issueId, {
        quantity: input.quantity,
      }),
    onSuccess: refresh,
  });
}
